import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.ts';
import type { DeckThumbsPayload } from '../../shared/api.ts';
import type { LayoutFinding, SlideEntry } from '../../shared/deck.ts';

// Deck-wide layout findings for the Activity tab.
//
// Piggybacks the thumbnail status the editor already warms — the measurement
// rides along with each screenshot (pipeline/slide-findings.ts), so this is a
// read of something that was going to be computed anyway. No separate scan, no
// separate endpoint.
//
// `warm` is what decides whether this RENDERS the deck or merely reads it, and
// the split matters because rendering here is the single most expensive thing
// the editor does on open: every slide, whether or not anyone looks at it.
// Everything else in the studio now generates only what is in view, and this is
// the one deliberate exception — the Activity tab lists the slides with layout
// problems, and a viewport-gated measurement would quietly under-report, which
// is worse than slow. So the coverage stays whole-deck and only the TIMING
// moves: cached findings are read on open (instant, free), and the generating
// poll waits until the Activity tab has actually been opened.
//
// The poll needs a TERMINAL CONDITION for the same reason every other /thumbs
// caller does: "loop while pending is non-empty" doesn't have one, and a deck
// with one unrenderable slide would relaunch Chromium every 800ms forever. So
// it stops on the server's backoff, on the pending count standing still, and on
// a wall-clock cap. Any CHANGE counts as progress, not just a decrease — a
// rebuild makes the count jump up, and treating that as a stall would declare a
// healthy deck stuck.

const POLL_MS = 1500;
const MAX_MS = 120_000;
const STALL_LIMIT = 6; // polls with no change at all

type FindingsState = {
  findings: Record<string, LayoutFinding[]>;
  pending: string[];
  loading: boolean;
};

export function useDeckFindings(slug: string | null | undefined, ready: boolean, warm = true) {
  const [state, setState] = useState<FindingsState>({ findings: {}, pending: [], loading: true });
  // Bumped to force a re-poll after something changes a slide.
  const [nonce, setNonce] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!slug || !ready) return;
    let cancelled = false;
    let lastPending: number | null = null;
    let stalls = 0;
    const startedAt = Date.now();

    const stop = () => {
      cancelled = true;
      clearTimeout(timer.current);
    };

    async function poll() {
      if (cancelled) return;
      let res: Partial<DeckThumbsPayload>;
      try {
        // Cold read until the tab is opened: shows whatever has already been
        // measured (by a previous session, a build, or another surface) without
        // rendering anything.
        res = await api.get<Partial<DeckThumbsPayload>>(`/api/decks/${slug}/thumbs${warm ? '' : '?generate=0'}`);
      } catch {
        return stop(); // the deck is gone or the server is down — nothing to wait for
      }
      if (cancelled) return;
      const pending = res.pending || [];
      setState({ findings: res.findings || {}, pending, loading: false });
      // A cold read is a SNAPSHOT, not a wait: nothing is rendering, so polling
      // would just re-read the same numbers until the stall budget ran out.
      if (!warm) return stop();
      if (pending.length === 0) return stop();
      if (res.coolingUntil && res.coolingUntil > Date.now()) return stop(); // server gave up
      if (Date.now() - startedAt > MAX_MS) return stop();
      if (lastPending !== null && pending.length === lastPending) {
        if (++stalls >= STALL_LIMIT) return stop();
      } else {
        stalls = 0;
      }
      lastPending = pending.length;
      timer.current = setTimeout(poll, POLL_MS);
    }
    poll();
    return stop;
  }, [slug, ready, nonce, warm]);

  return { ...state, refresh };
}

/** Slides with at least one finding, in deck order — what the Activity tab
 *  lists. Slides that have not been measured yet are simply absent, which is
 *  why the panel says how many are still being checked rather than implying a
 *  clean bill of health. */
export function flaggedSlides(
  findings: Record<string, LayoutFinding[]> | null | undefined,
  slides: SlideEntry[] | null | undefined,
): { slide: SlideEntry; findings: LayoutFinding[] }[] {
  const out: { slide: SlideEntry; findings: LayoutFinding[] }[] = [];
  for (const s of slides || []) {
    const f = findings?.[s.id];
    if (f && f.length > 0) out.push({ slide: s, findings: f });
  }
  return out;
}
